import React, { useRef, useEffect } from "react";

const ScrollSyncWrapper = ({ children }) => {
  /* ─────────── refs ─────────── */
  const topRef = useRef(null); // top scrollbar div
  const bottomRef = useRef(null); // bottom scrollbar div
  const tableWrap = useRef(null); // wraps the <Table>
  const ghostRef = useRef(null); // stretches top bar width

  /* 1️⃣  keep ghost width == table width */
  useEffect(() => {
    const updateWidth = () => {
      if (tableWrap.current && ghostRef.current) {
        ghostRef.current.style.width = `${tableWrap.current.scrollWidth}px`;
      }
    };

    updateWidth(); // on mount

    const ro = new ResizeObserver(updateWidth); // on column resize
    if (tableWrap.current) ro.observe(tableWrap.current);
    return () => ro.disconnect();
  }, []);

  /* 2️⃣  hard sync scrollLeft both ways (no animation, no lag) */
  useEffect(() => {
    const top = topRef.current;
    const bot = bottomRef.current;
    if (!top || !bot) return;

    let syncing = false; // feedback‑loop guard

    const fromTop = () => {
      if (syncing) return;
      syncing = true;
      bot.scrollLeft = top.scrollLeft;
      syncing = false;
    };
    const fromBot = () => {
      if (syncing) return;
      syncing = true;
      top.scrollLeft = bot.scrollLeft;
      syncing = false;
    };

    top.addEventListener("scroll", fromTop);
    bot.addEventListener("scroll", fromBot);
    return () => {
      top.removeEventListener("scroll", fromTop);
      bot.removeEventListener("scroll", fromBot);
    };
  }, []);

  /* 3️⃣  wheel → horizontal scroll (instant) */
  useEffect(() => {
    const bot = bottomRef.current;
    if (!bot) return;

    const onWheel = (e) => {
      if (e.deltaY !== 0) {
        e.preventDefault(); // block vertical page scroll
        bot.scrollLeft += e.deltaY; // plain, immediate move
        /* top bar follows via the scroll listener */
      }
    };

    bot.addEventListener("wheel", onWheel, { passive: false });
    return () => bot.removeEventListener("wheel", onWheel);
  }, []);

  /* ─────────── render ─────────── */
  return (
    <>
      {/* top faux scrollbar */}
      <div
        ref={topRef}
        style={{
          overflowX: "auto",
          overflowY: "hidden",
          height: 16,
          marginBottom: 8,
        }}
      >
        <div ref={ghostRef} style={{ height: 1 }} />
      </div>

      {/* bottom real scroll container */}
      <div ref={bottomRef} style={{ overflowX: "auto", overflowY: "hidden" }}>
        <div ref={tableWrap}>{children}</div>
      </div>
    </>
  );
};

export default ScrollSyncWrapper;
